import { TopicDialog, TopicsMoreInfoDialog } from '@communities/content-management/components';
import type { Topic, EntityType } from '@communities/content-management/domain';
import { useDeleteCommunityGroupTopic, useDeleteCommunityTopic, useCommunityGroupTopics, useCommunityTopics } from '@communities/content-management/hooks';
import { formatSeconds, getEntityDisplayName, isCommunity } from '@communities/content-management/utils';
import { DataList, type TableColumn } from '@core/components';
import { SectionHeader } from '@core/components';
import { withAppInsightsTracking } from '@core/logging/withAppInsightsTracking';
import { Button, IconButton, Info16, IconButtonSize, IconSize, Section } from '@soteria/design-system-ve';
import { useState, useCallback } from 'react';

interface TopicsTabProps {
  entityId: string;
  entityType: EntityType;
  isEntityAdmin: boolean;
  sectionTitle?: string;
  showModerationSettings?: boolean;
}

// eslint-disable-next-line complexity
function TopicsTabComponent({ entityId, entityType, isEntityAdmin, sectionTitle, showModerationSettings = false }: TopicsTabProps) {
  const [selectedTopic, setSelectedTopic] = useState<Topic | null>(null);
  const [isTopicDialogOpen, setIsTopicDialogOpen] = useState(false);
  const [isMoreInfoDialogOpen, setIsMoreInfoDialogOpen] = useState(false);

  // Hooks for getting topics
  const communityHook = useCommunityTopics(isCommunity(entityType) ? entityId : undefined);
  const communityGroupHook = useCommunityGroupTopics(!isCommunity(entityType) ? entityId : undefined);

  // Hooks for deleting topics
  const communityDeleteTopicHook = useDeleteCommunityTopic(isCommunity(entityType) ? entityId : undefined);
  const communityGroupDeleteTopicHook = useDeleteCommunityGroupTopic(!isCommunity(entityType) ? entityId : undefined);

  const { topics, loading, errors } = isCommunity(entityType) ? communityHook : communityGroupHook;
  const {
    deleteTopic,
    loading: isDeleting,
    errors: deleteErrors,
    resetErrors,
  } = isCommunity(entityType) ? communityDeleteTopicHook : communityGroupDeleteTopicHook;

  const columns: TableColumn<Topic>[] = [
    {
      key: 'name',
      header: 'Topic Name',
      render: topic => topic.name,
      width: '2fr',
    },
    ...(showModerationSettings
      ? [
          {
            key: 'assignedTtl',
            header: 'Assigned TTL',
            render: (topic: Topic) => formatSeconds(topic.humanModerationAssignedTtlInSeconds),
            width: '1fr',
          },
          {
            key: 'maxCases',
            header: 'Max Cases Per Moderator',
            render: (topic: Topic) => topic.maxCasesPerModerator.toString(),
            width: '1fr',
          },
          {
            key: 'slaTime',
            header: 'SLA Time',
            render: (topic: Topic) => formatSeconds(topic.humanModerationCasesSlaInSeconds),
            width: '1fr',
          },
        ]
      : []),
  ];

  const handleOpenDialog = useCallback((topic?: Topic) => {
    setSelectedTopic(topic || null);
    setIsTopicDialogOpen(true);
  }, []);

  const handleCloseDialog = useCallback(() => {
    setIsTopicDialogOpen(false);
    setSelectedTopic(null);
  }, []);

  // Create the delete item context
  const deleteItemContext = isEntityAdmin
    ? {
        handler: async (item: Topic) => await deleteTopic(entityId, item.name),
        isDeleting,
        errors: deleteErrors,
        resetErrors,
      }
    : undefined;

  return (
    <>
      <Section spacing="large">
        <SectionHeader
          description={`Configure topics for this ${getEntityDisplayName(entityType)}. `}
          endComponent={
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <IconButton
                aria-label="Learn more about topics"
                buttonSize={IconButtonSize.Medium}
                icon={Info16}
                iconSize={IconSize.XSmall}
                onClick={() => setIsMoreInfoDialogOpen(true)}
              />
              {isEntityAdmin && !showModerationSettings && <Button onClick={() => handleOpenDialog()}>ADD TOPIC</Button>}
            </div>
          }
          title={`${sectionTitle || 'Topics'} (${topics.length})`}
        />

        {/* Topics List*/}
        <DataList
          columns={columns}
          data={topics}
          dataTypeName="topic"
          deleteItemContext={deleteItemContext}
          error={errors}
          isLoading={loading}
          onEdit={isEntityAdmin ? handleOpenDialog : undefined}
        />
      </Section>

      {/* Create/Edit Topic Dialog */}
      <TopicDialog
        editMode={showModerationSettings ? 'moderation' : 'name'}
        entityId={entityId}
        entityType={entityType}
        isOpen={isTopicDialogOpen}
        topic={selectedTopic || undefined}
        onClose={handleCloseDialog}
      />

      {/* More Info Dialog */}
      <TopicsMoreInfoDialog isOpen={isMoreInfoDialogOpen} onClose={() => setIsMoreInfoDialogOpen(false)} />
    </>
  );
}

export const TopicsTab = withAppInsightsTracking(TopicsTabComponent, 'TopicsTab');
